/**
 * Phase 5a: partnership and LLP partner/member profit allocation and capital/current/loan accounts.
 * Mirrors Contracts/Practice/Phase5/PartnerAllocationContracts.cs.
 */

/** Client types, as the API uses them. */
export enum PartnerClientType {
  Limited = 0,
  SoleTrader = 1,
  Partnership = 2,
  Llp = 3,
}

/** Roles: 1 Partner, 2 Salaried partner, 3 Designated member, 4 Member. */
export interface PartnerOption {
  id: number;
  name: string;
}

export interface PartnerMemberRow {
  id: string;
  fullName: string;
  role: number;
  roleName: string;
  joinedDate: string | null;
  leftDate: string | null;
  profitSharePercent: number;
  fixedShareUnits: number;
  salaryAmount: number;
  interestOnCapitalRate: number;
  utr: string | null;
  addressLine: string | null;
  city: string | null;
  postCode: string | null;
  sortOrder: number;
}

export interface PartnerMemberScreen {
  clientId: string;
  clientName: string;
  clientType: number;
  isPartnershipOrLlp: boolean;
  entityLabel: string;
  partners: PartnerMemberRow[];
  roles: PartnerOption[];
}

export interface SavePartnerMemberPayload {
  clientId: string;
  id: string | null;
  fullName: string;
  role: number;
  joinedDate: string | null;
  leftDate: string | null;
  profitSharePercent: number;
  fixedShareUnits: number;
  salaryAmount: number;
  interestOnCapitalRate: number;
  utr: string | null;
  addressLine: string | null;
  city: string | null;
  postCode: string | null;
}

// ---- Profit allocation ----

/** Allocation bases: 0 percentage, 1 fixed share, 2 salary and interest then balance, 3 manual. */
export enum AllocationBasis {
  Percentage = 0,
  FixedShare = 1,
  SalaryAndInterest = 2,
  Manual = 3,
}

export interface ProfitAllocationRow {
  relationshipId: string;
  fullName: string;
  role: number;
  roleName: string;
  joinedDate: string | null;
  leftDate: string | null;
  sharePercent: number;
  fixedShareUnits: number;
  salaryAmount: number;
  interestOnCapitalRate: number;
  manualAmount: number;
  daysInPeriod: number;
  effectiveDays: number;
  effectivePortion: number;
  allocatedSalary: number;
  allocatedInterest: number;
  allocatedBalance: number;
  roundingAdjustment: number;
  allocatedAmount: number;
  /** cap3-e: drawings the interest was charged on, and the interest deducted from this partner's allocation. */
  drawings?: number;
  allocatedDrawingsInterest?: number;
}

export interface ProfitAllocationScreen {
  clientId: string;
  clientName: string;
  clientType: number;
  isPartnershipOrLlp: boolean;
  entityLabel: string;
  accountingPeriodId: string | null;
  periodLabel: string;
  isLocked: boolean;
  hasTrialBalance: boolean;
  basis: number;
  roundingRelationshipId: string | null;
  profitAvailable: number;
  totalAllocated: number;
  difference: number;
  isReconciled: boolean;
  reconciliationStatus: string;
  calculatedAtUtc: string | null;
  rows: ProfitAllocationRow[];
  bases: PartnerOption[];
  /** cap3-e: rate (%) charged on partners' drawings for this run; 0 charges nothing. */
  interestOnDrawingsRate?: number;
}

export interface ProfitAllocationInput {
  relationshipId: string;
  sharePercent: number;
  fixedShareUnits: number;
  salaryAmount: number;
  interestOnCapitalRate: number;
  manualAmount: number;
}

export interface SaveProfitAllocationPayload {
  clientId: string;
  accountingPeriodId: string;
  basis: number;
  roundingRelationshipId: string | null;
  rows: ProfitAllocationInput[];
  /** cap3-e: omit to keep the saved rate. */
  interestOnDrawingsRate?: number | null;
}

export interface CalculateProfitAllocationPayload {
  clientId: string;
  accountingPeriodId: string;
  profitOverride: number | null;
  /** cap3-e: omit to use the saved rate. */
  interestOnDrawingsRate?: number | null;
}

// ---- Capital, current and loan accounts ----

/** Account kinds: 1 Capital, 2 Current, 3 Loan. */
export enum PartnerAccountKind {
  Capital = 1,
  Current = 2,
  Loan = 3,
}

export interface PartnerMemberBalanceRow {
  relationshipId: string;
  fullName: string;
  roleName: string;
  accountKind: number;
  accountKindName: string;
  openingBalance: number;
  introduced: number;
  allocationMovement: number;
  interestMovement: number;
  drawings: number;
  otherMovement: number;
  closingBalance: number;
  /** cap3-e: interest charged on drawings (a deduction on the current account). */
  drawingsInterestCharged?: number;
}

export interface PartnerBalanceReconciliation {
  totalClosingBalance: number;
  equityNet: number;
  difference: number;
  isReconciled: boolean;
  status: string;
}

export interface PartnerMemberBalanceScreen {
  clientId: string;
  clientName: string;
  clientType: number;
  isPartnershipOrLlp: boolean;
  entityLabel: string;
  accountingPeriodId: string | null;
  periodLabel: string;
  isLocked: boolean;
  rows: PartnerMemberBalanceRow[];
  accountKinds: PartnerOption[];
  reconciliation: PartnerBalanceReconciliation;
}

export interface PartnerMemberBalanceInput {
  relationshipId: string;
  accountKind: number;
  openingBalance: number;
  introduced: number;
  drawings: number;
  otherMovement: number;
}

export interface SavePartnerMemberBalancesPayload {
  clientId: string;
  accountingPeriodId: string;
  rows: PartnerMemberBalanceInput[];
}

// ---- Disclosures ----

export interface PartnerStatementLine {
  label: string;
  values: string[];
  style: string;
}

export interface PartnerStatement {
  title: string;
  headers: string[];
  lines: PartnerStatementLine[];
}

export interface PartnerDisclosureView {
  clientId: string;
  clientName: string;
  clientType: number;
  isPartnershipOrLlp: boolean;
  entityLabel: string;
  accountingPeriodId: string | null;
  periodLabel: string;
  notes: string[];
  statements: PartnerStatement[];
}

/** Money for the grids: two decimals, negatives in brackets, zero as a dash. */
export function formatPartnerMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) {
    return '-';
  }
  const text = Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return value < 0 ? `(${text})` : text;
}

/** Reads a grid input back as a number without turning a blank cell into NaN. */
export function toAmount(value: unknown): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
